import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import { PullRequestDetails, isLeaseActive, RolloutSetupOptions } from '@finvari/deliver';
import { useConfig } from '../../../contexts/ConfigContext';
import { useDeploymentStatus } from '../../../contexts/DeploymentStatusContext';
import { useConfiguredEnvironments, useVisibleEnvironments } from '../../../contexts/EnvironmentsContext';
import { usePools } from '../../../contexts/PoolsContext';
import { useEnvironment } from '../../../hooks/useEnvironments';
import { createDeploymentForPullRequest } from '../../../firebase';
import DeploymentOptionsDialog from '../../../components/DeploymentOptionsDialog';
import QueuedDeploysList from '../../../components/QueuedDeploysList';

interface EnvironmentsTabProps {
  pr: PullRequestDetails;
}

const EnvironmentsTab: React.FC<EnvironmentsTabProps> = ({ pr }) => {
  const prNumber = pr.pr.number;
  const headSha = pr.pr.head?.sha;

  const { config, userProfile } = useConfig();
  const { liveDeploymentsByEnvironment } = useDeploymentStatus();
  const { environmentsById, loading: environmentsLoading } = useConfiguredEnvironments();
  const environments = useVisibleEnvironments();
  const { pools, queuedRollouts } = usePools();

  // Environments where this PR is currently live (the panel's read-only status list). The
  // LiveDeployment shape isn't exported from the context, so it's read structurally here.
  const liveHere = useMemo(() => {
    const out: Array<{ environmentId: string; live: any }> = [];
    liveDeploymentsByEnvironment.forEach((live, environmentId) => {
      if (live.prNumber === prNumber) out.push({ environmentId, live });
    });
    return out;
  }, [liveDeploymentsByEnvironment, prNumber]);

  const heldEnvironment = useMemo(
    () =>
      Object.values(environmentsById).find(
        e => e.poolId && isLeaseActive(e.lease) && e.lease?.holderUid === userProfile?.uid
      ),
    [environmentsById, userProfile?.uid]
  );

  const [selectedTarget, setSelectedTarget] = useState('');
  const selectedPoolId = selectedTarget.startsWith('pool:') ? selectedTarget.slice(5) : undefined;
  const selectedEnvironment = selectedPoolId ? '' : selectedTarget;
  const [deploying, setDeploying] = useState(false);
  const [deployError, setDeployError] = useState<string | null>(null);
  const [deploySuccess, setDeploySuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { hasAnyOperationInFlight } = useEnvironment(selectedEnvironment);

  useEffect(() => {
    if (!selectedTarget) {
      const defaultId = userProfile?.defaultEnvironmentId;
      const resolvedId =
        defaultId && environmentsById[defaultId] && !environmentsById[defaultId].poolId
          ? defaultId
          : environments[0]?.gcpProjectId ?? (pools[0] ? `pool:${pools[0].poolId}` : '');
      if (resolvedId) setSelectedTarget(resolvedId);
    }
  }, [environments, environmentsById, pools, userProfile, selectedTarget]);

  const handleDeploy = async (
    setupOptions?: RolloutSetupOptions,
    selectedModules?: string[],
    autoDeployOnPush?: boolean
  ) => {
    setDeploying(true);
    setDeployError(null);
    setDeploySuccess(null);
    try {
      if (selectedPoolId) {
        const res: any = await createDeploymentForPullRequest(
          prNumber, '', selectedModules, setupOptions, selectedPoolId, autoDeployOnPush
        );
        const data = res?.data ?? {};
        setDeploySuccess(data.message || `Deploy queued or started on ${data.environment ?? 'a pooled environment'}.`);
      } else {
        await createDeploymentForPullRequest(
          prNumber, selectedEnvironment, selectedModules, setupOptions, undefined, autoDeployOnPush
        );
        setDeploySuccess(`Deploy started on ${selectedEnvironment}.`);
      }
    } catch (err: any) {
      setDeployError(err.message || `Failed to start deployments for PR #${prNumber}`);
    } finally {
      setDeploying(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Live environments */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>Live environments</Typography>
        {liveHere.length === 0 ? (
          <Typography variant="body2" color="text.secondary">This PR is not currently deployed anywhere.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {liveHere.map(({ environmentId, live }) => {
              const modules = Array.from(live.deployments.keys()) as string[];
              const anyDeployment = Array.from(live.deployments.values())[0] as any;
              const deployedCommit: string | undefined = anyDeployment?.commitHash;
              const upToDate = deployedCommit && headSha && deployedCommit === headSha;
              const autodeploy = environmentsById[environmentId]?.lease?.autodeploy;
              return (
                <Paper key={environmentId} variant="outlined" sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1 }}>
                      {environmentsById[environmentId]?.name || environmentId}
                    </Typography>
                    <Chip
                      size="small"
                      color={upToDate ? 'success' : 'warning'}
                      label={upToDate ? 'Up to date' : 'Behind'}
                    />
                    {autodeploy && <Chip size="small" variant="outlined" label="autodeploy" />}
                    <Link component={RouterLink} to={`/environment/${environmentId}`}>Open</Link>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {modules.join(', ') || 'no modules'}
                    {deployedCommit ? ` · @ ${deployedCommit.slice(0, 7)}` : ''}
                  </Typography>
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>

      <Divider />

      {/* Deploy control (moved from the old detail sidebar) */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>Deploy this PR</Typography>
        {deployError && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setDeployError(null)}>{deployError}</Alert>}
        {deploySuccess && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setDeploySuccess(null)}>{deploySuccess}</Alert>}

        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
          <InputLabel>Deploy target</InputLabel>
          <Select
            value={selectedTarget}
            label="Deploy target"
            onChange={e => setSelectedTarget(e.target.value)}
            disabled={deploying}
          >
            {environmentsLoading && <MenuItem disabled>Loading environments…</MenuItem>}
            {!environmentsLoading && environments.length === 0 && pools.length === 0 && !heldEnvironment && (
              <MenuItem disabled>No deploy targets available</MenuItem>
            )}
            {environments.map(env => (
              <MenuItem key={env.gcpProjectId} value={env.gcpProjectId}>{env.name} ({env.gcpProjectId})</MenuItem>
            ))}
            {heldEnvironment && (
              <MenuItem value={heldEnvironment.gcpProjectId}>{heldEnvironment.name} (you hold this lease)</MenuItem>
            )}
            {pools.map(pool => (
              <MenuItem key={pool.poolId} value={`pool:${pool.poolId}`}>Pool: {pool.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {hasAnyOperationInFlight && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Environment has active operations. Wait for them to complete before deploying.
          </Alert>
        )}

        <Button
          variant="contained"
          size="small"
          onClick={() => setDialogOpen(true)}
          disabled={deploying || !selectedTarget || (!selectedPoolId && hasAnyOperationInFlight)}
        >
          {deploying ? <><CircularProgress size={16} sx={{ mr: 1 }} />Deploying…</> : 'Run deployment'}
        </Button>

        {(() => {
          const prQueued = queuedRollouts.filter(r => r.prId === pr.pr.id);
          return prQueued.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">Queued pool deploys</Typography>
              <QueuedDeploysList rollouts={prQueued} showPool />
            </Box>
          ) : null;
        })()}
      </Box>

      <DeploymentOptionsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        environmentId={selectedEnvironment}
        poolId={selectedPoolId}
        environmentName={
          selectedPoolId
            ? `Pool: ${pools.find(p => p.poolId === selectedPoolId)?.name ?? selectedPoolId}`
            : environmentsById[selectedEnvironment]?.name ?? selectedEnvironment
        }
        availableModules={config?.modules}
        onDeployOnly={(selectedModules, autoDeployOnPush) => handleDeploy(undefined, selectedModules, autoDeployOnPush)}
        onDeployAndPrep={(options, selectedModules, autoDeployOnPush) => handleDeploy(options, selectedModules, autoDeployOnPush)}
      />
    </Box>
  );
};

export default EnvironmentsTab;
